import { TypographyVariant } from '@kite-js/experimental/lib/Typography/variant';
import { FeatureId } from '@agoda/layout';
import { LoyaltySearchType } from 'core/type/LoyaltySearch.types';
import LogoMap from '@kite-js/logo/lib/Logo/LogoMap';
// eslint-disable-next-line no-restricted-imports
import { CartFeaturesSettings } from '@cart-js/core';
import { FeatureName } from 'core/constant/FeatureName';

export enum MENU_ITEM {
    LAZY_ITEM = 'LAZY_ITEM',
    AGODA_HEADER_ITEM = 'AGODA_HEADER_ITEM',
    DEFAULT_ITEM = 'DEFAULT_ITEM',
}

export interface ActivitiesBehavior {
    common: CommonBehavior;
    header: HeaderBehavior;
    footer: FooterBehavior;
    home: HomePageBehavior;
    search: SearchPageBehavior;
    detail: DetailPageBehavior;
    city: CityPageBehavior;
    availability: AvailabilityPageBehavior;
    homeImageUrl: string;
    cartFeatures: CartFeatures;
    tripSavings: TripSavingsBehavior;
    featureExperiments: FeatureExperiments;
}

export interface CommonBehavior {
    /** Support for burning (using) points */
    burn: PointsBehavior;

    /** Support for earning (gaining) points */
    earn: PointsBehavior;
    price: PriceBehavior;
    useUniversalLoginForm: boolean;
    shoppingCart: boolean;
    sessionPackage: SessionPackageBehavior;
    userSessionManagement: UserSessionManagementBehavior;
    isSundayFirstDayOfWeek: boolean;
    showEarnBurnToggle: boolean;
    partnerLoyaltyConfiguration: PartnerLoyaltyBehavior;
    ensightenTagsConfiguration: EnsightenTagsBehavior;
    isRtaFooterEnabled: boolean;
    isDefaultLanguageFromConfig: boolean;
    isCartInterstitialEnabled: boolean;
    isLogoutWithCronosEnabled: boolean;
    shouldUseXLIcon: boolean;
    hideFreeCancellation: boolean;
    showInclusiveTax: boolean;
    activitiesWhitelabelIdMapping: ActivitiesWhitelabelIdMapping;
}

export enum PriceDisplayType {
    PerBook,
    PerPax,
}

export enum PriceDisplayBehavior {
    Price,
    PriceAndPoints,
    PriceWithPointsOnNextLine,
    PriceWithPointsOnSameLine,
    PriceWithBadgePoints,
}

export enum PriceCrossedOutRateBehavior {
    NotSupported,
    Supported,
}

export interface PriceBehavior {
    crossedOutRateBehavior: PriceCrossedOutRateBehavior;
    displayBehavior: PriceDisplayBehavior;
    displayType: PriceDisplayType;
    prefixCms: number;
    priceTypography: TypographyVariant;
    pointsTypography: TypographyVariant;
    roundingPriceBehavior: boolean;
    ssrDisplayBehavior: PriceDisplayBehavior;
    isPointAtFirst: boolean;
    currencyVariant: string;
    currencyTextColor: string;
    amountVariant: string;
    amountTextColor: string;
}

export interface PointsBehavior {
    isEnabled: boolean;
    pointsCms: number;
    pointsWithExtraCms?: number;
    pointsVariant: string;
    pointsColor: string;
    pointsTextVariant: string;
    pointsTextColor: string;
}

export interface HeaderBehavior {
    vip: boolean;
    signUp: boolean;
    listYourPlace: boolean;
    showOnlyPointsInUserMenu: boolean;
    show: boolean;
    showEarnAcceleratorBanner: boolean;
    featureConfigs: Partial<Record<FeatureId, Record<string, string>>>;
    isResponsiveHeaderEnabled: boolean;
    menuItemType: MENU_ITEM;
    isBlackHeaderBackground: boolean;
}

export enum FooterType {
    B2C_FOOTER = 'B2C_FOOTER',
    B2B_FOOTER = 'B2B_FOOTER',
}

export interface FooterBehavior {
    footerEnum: FooterType;
    poweredByLogo: keyof typeof LogoMap;
    poweredByLogoAltCms: number;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface HomePageBehavior {
    openDetailPageInNewTab: boolean;
    showFooter: boolean;
    showWhyChooseUs: boolean;
    unifiedCrossSellCarousel: boolean;
}

export interface SearchPageBehavior {
    openDetailPageInNewTab: boolean;
    showFooter: boolean;
    showDealsBadges: boolean;
}

export interface DetailPageBehavior {
    showFooter: boolean;
    hidePricesInCalendar: boolean;
}

export interface CityPageBehavior {
    showFooter: boolean;
    showPillShapedCategories: boolean;
}

export enum OfferStartTimeDisplayBehavior {
    TwentyFourHour,
    TwelveHour,
}

export interface AvailabilityPageBehavior {
    offerStartTimeFormat: OfferStartTimeDisplayBehavior;
    bookingOptions: BookingOptionsBehavior;
}

export interface BookingOptionsBehavior {
    summaryCms: number;
}

export interface SessionPackageBehavior {
    isEnabled: boolean;
    partnerConfig?: PartnerConfig;
}

export interface UserSessionManagementBehavior {
    isEnabled: boolean;
    config?: UserSessionManagementConfig;
}

export interface PartnerLoyaltyBehavior {
    isEnabled: boolean;
    config?: PartnerLoyaltyConfiguration;
}

export interface EnsightenTagsBehavior {
    isEnabled: boolean;
    config?: EnsightenTagsConfiguration;
}

export interface PartnerConfig {
    session: PartnerConfigSession;
    mockEnabled: boolean;
    loyaltyUnit: PartnerConfigLoyaltyUnit;
}

export interface UserSessionManagementConfig {
    refreshGapInSec: number;
    forceLogoutEnable: boolean;
    excludedPageTypeIds: number[];
    tokenExpiryInSec: number;
    onPromptTimeInSec: number;
    intervalTimeInSec: number;
    idleThresholdInSec?: number;
}

export interface PartnerLoyaltyConfiguration {
    defaultLoyaltyType?: LoyaltySearchType;
}

export interface ActivitiesWhitelabelIdMapping {
    targetWhitelabelId?: number;
}

export interface EnsightenTagsConfiguration {
    path: string;
    events?: Record<string, EnsightenEventConfig>;
}

export enum EnsightenTagEventKey {
    SEARCH_RESULTS_PAGE_VIEW = 'activitiessearchresultspageview',
}

export type PartnerConfigSession = UserSessionManagementConfig;

export interface PartnerConfigLoyaltyUnit {
    cmsId: number;
    value: string;
}

export interface EnsightenEventConfig {
    eventId: string;
    actionType: string;
    evarpagename: string;
    siteEvents: string;
    inlineMessage: string;
    testId: string;
    pageType: string;
}

export interface CartFeatures {
    activitiesOriginRestriction: boolean;
    enableTotalPriceInSlider: boolean;
    enableCartDesktopInterstitialV2: boolean;
    addAnyItemToCart: boolean;
    displayCartPriceInPoints: CartFeaturesSettings['DisplayCartPriceInPoints'];
    shoppingCartIconStyle: CartFeaturesSettings['ShoppingCartIconStyle'];
    taxAndFeeLabelInCart: CartFeaturesSettings['TaxAndFeeLabelInCart'];
    rebrandCartToSaved: boolean;
    rebrandCartToSavedWebview: boolean;
}

export interface TripSavingsBehavior {
    unlockTripSavings: boolean;
}

export interface FeatureExperiments {
    featureBehavior: Record<FeatureName, boolean>;
}
